import { z } from 'zod';

/**
 * The identity of the feature on whose behalf an AI call is made — the unit the
 * rate limiter windows, the cache key salts, and the cost dashboard groups by.
 *
 * **It is a validated pattern, not a closed union**, and that is deliberate.
 * P8 ships the platform with no features of its own; the consumers arrive in
 * later phases (chat moderation, face-match, photo checks). A union whose every
 * member has no producer is a fiction that the phase declaring it cannot honour,
 * and enumerating keys for unwritten code freezes vocabulary decisions those
 * phases have not made yet.
 *
 * The discipline that replaces the union: **each consumer exports its key as a
 * named constant in its own module** (`export const MODERATION_CHAT_FEATURE =
 * 'moderation.chat-message'`) and passes that constant, never a literal — so a
 * typo is still a compile error at every call site, without this file having to
 * predict the list. The admin dashboard groups by the distinct keys actually
 * observed in `ai_usage_logs`, which is the only truthful list there is.
 *
 * The shape is dot-separated lower-kebab (`<module>.<feature>`), matching the
 * permission-key grammar so the two read alike.
 */
const FEATURE_KEY_PATTERN = /^[a-z][a-z0-9]*(?:-[a-z0-9]+)*(?:\.[a-z][a-z0-9]*(?:-[a-z0-9]+)*)+$/;

export const AiFeatureKeySchema = z
  .string()
  .min(3)
  .max(64)
  .regex(
    FEATURE_KEY_PATTERN,
    'An AI feature key is dot-separated lower-kebab, e.g. "moderation.chat-message".',
  );

export type AiFeatureKey = z.infer<typeof AiFeatureKeySchema>;

/** Narrows a stored column or a client-supplied filter value. */
export function isAiFeatureKey(value: string): value is AiFeatureKey {
  return AiFeatureKeySchema.safeParse(value).success;
}
